import { useState } from "react";
import {
  Card,
  DatePicker,
  Select,
  Input,
  Button,
  Table,
  Tag,
  Modal,
  Spin,
  Descriptions,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { useQuery } from "@tanstack/react-query";
import ActivityLogsService from "@/services/APIs/ActivityLogsService";

interface ActivityLog {
  id: string;
  user_name: string;
  activity_type: string;
  activity_title: string;
  created_on: string;
}

const ErrorLogs: React.FC = () => {
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [activityType, setActivityType] = useState<string | undefined>(
    undefined
  );
  const [searchText, setSearchText] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);

  // Fetch list data using React Query
  const { data: logsData, isLoading } = useQuery({
    queryKey: [
      "activityLogs",
      page,
      pageSize,
      fromDate,
      toDate,
      activityType,
      searchText,
    ],
    queryFn: () =>
      ActivityLogsService.getAllActivityLogs({
        page,
        pageSize,
        start_date: fromDate ? fromDate.format("YYYY-MM-DD") : undefined,
        end_date: toDate ? toDate.format("YYYY-MM-DD") : undefined,
        activity_type: activityType,
        search: searchText,
      }),
  });

  // Fetch detail data using React Query
  const { data: logDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ["activityLogDetail", selectedLogId],
    queryFn: () => ActivityLogsService.getActivityLogById(selectedLogId!),
    enabled: !!selectedLogId && isModalOpen,
  });

  const dataSource: ActivityLog[] =
    logsData?.results?.data ?? logsData?.results ?? [];
  const totalItems = logsData?.count ?? 0;

  const columns: ColumnsType<ActivityLog> = [
    {
      title: "Date & Time",
      dataIndex: "created_on",
      key: "created_on",
      width: 180,
      render: (date: string) => dayjs(date).format("DD MMM, YYYY hh:mm A"),
    },
    {
      title: "User",
      dataIndex: "user_name",
      key: "user_name",
      width: 250,
    },
    {
      title: "Activity Type",
      dataIndex: "activity_type",
      key: "activity_type",
      width: 150,
      render: (type: string) => {
        let color = "blue";
        if (type === "Error") {
          color = "red";
        } else if (type === "Warning") {
          color = "orange";
        } else if (type === "Login") {
          color = "green";
        } else if (type === "Logout") {
          color = "default";
        }
        return <Tag color={color}>{type}</Tag>;
      },
    },
    {
      title: "Description",
      dataIndex: "activity_title",
      key: "activity_title",
      ellipsis: true,
    },
    {
      title: "",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
          onClick={() => {
            setSelectedLogId(record.id);
            setIsModalOpen(true);
          }}
        >
          Details
        </Button>
      ),
    },
  ];

  const handleClear = () => {
    setFromDate(null);
    setToDate(null);
    setActivityType(undefined);
    setSearchText("");
    setPage(1);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedLogId(null);
  };

  return (
    <div className="space-y-4 p-2 sm:space-y-6 sm:p-4">
      <div className="flex flex-col items-start justify-between sm:flex-row sm:items-center">
        <div>
          <h1 className="text-xl font-bold sm:text-2xl lg:text-3xl">
            Error Logs
          </h1>
          <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
            View and manage system activity logs.
          </p>
        </div>
      </div>
      <Card
        bordered={false}
        style={{
          boxShadow:
            "0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)",
        }}
        styles={{ body: { padding: 0 } }}
      >
        {/* Filter Section */}
        <div style={{ padding: "12px 16px 0" }} className="sm:!p-6 sm:!pb-0">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 8,
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                From Date
              </label>
              <DatePicker
                value={fromDate}
                onChange={date => {
                  setPage(1);
                  setFromDate(date);
                }}
                format="DD MMM, YYYY"
                style={{ width: "100%" }}
                placeholder="Select date"
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 8,
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                To Date
              </label>
              <DatePicker
                value={toDate}
                onChange={date => {
                  setPage(1);
                  setToDate(date);
                }}
                format="DD MMM, YYYY"
                style={{ width: "100%" }}
                placeholder="Select date"
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 8,
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Activity Type
              </label>
              <Select
                value={activityType}
                onChange={value => {
                  setPage(1);
                  setActivityType(value);
                }}
                style={{ width: "100%" }}
                placeholder="Select type"
                allowClear
                options={[
                  { value: "Login", label: "Login" },
                  { value: "Logout", label: "Logout" },
                  { value: "Error", label: "Error" },
                  { value: "Performance", label: "Performance" },
                ]}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 8,
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Search Text
              </label>
              <Input
                value={searchText}
                onChange={e => {
                  setPage(1);
                  setSearchText(e.target.value);
                }}
                placeholder="Search your text..."
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <Button onClick={handleClear}>Clear</Button>
          </div>
        </div>

        {/* Table Section */}
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: totalItems,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]} - ${range[1]} of ${total} items`,
            pageSizeOptions: ["10", "50", "100", "200"],
          }}
          bordered
          style={{ marginTop: 0 }}
        />
      </Card>

      <Modal
        title="Activity Log Details"
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={[
          <Button key="close" onClick={handleModalClose}>
            Close
          </Button>,
        ]}
        width={900}
      >
        {isLoadingDetail ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin size="large" />
          </div>
        ) : logDetail ? (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="User Name">
              {logDetail.user_name}
            </Descriptions.Item>
            <Descriptions.Item label="Activity Type">
              <Tag color="blue">{logDetail.activity_type}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Activity Title">
              {logDetail.activity_title}
            </Descriptions.Item>
            <Descriptions.Item label="Activity Description">
              {logDetail.activity_description ?? "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Date & Time">
              {dayjs(logDetail.created_on).format("DD MMM, YYYY hh:mm A")}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <p>No details found.</p>
        )}
      </Modal>
    </div>
  );
};

export default ErrorLogs;
